<template>
  <div
    :class="[
      'CustomProposalCard',
      {
        'is-mobile': isMobile,
        'is-ios': isIOS,
      },
    ]"
  >
    <div class="cover">
      <LocationCover v-if="chosenLocation" :location="chosenLocation" is-colorful class="location-image" />

      <div
        ref="cardOptionsRef"
        class="card-options"
        :class="{ 'is-open': isOptionsOpen }"
        @click.self="isOptionsOpen = false"
      >
        <div v-if="isOptionsOpen" class="card-options-close" @click="isOptionsOpen = false">
          <GamIcon :name="GamIconName.X_CLOSE" :size="GamIconSize.NORMAL" is-current />
        </div>

        <div v-if="isOptionsOpen" class="card-options-scroll">
          <div class="options-expanded">
            <input v-model="title" placeholder="Title" class="inline-title-input" type="text" autocomplete="off" />
            <GambitTypeSelector
              v-model="gambitType"
              :options="tagOptions"
              is-streaming-hidden
              is-compact
              class="inline-tags"
            />
          </div>
        </div>
      </div>

      <div class="cover-foot">
        <div class="cover-players">
          <div v-for="(slot, i) in playerCircles" :key="`circle-${i}`" class="seat">
            <PlayerAvatar v-if="slot" :player="slot" is-invited is-stacked hide-rating />

            <div v-else-if="i === playerCircles.length - 1" class="seat-ring is-invite" @click="handleOpenInviteModal">
              <span v-if="selectedPlayers.length" class="seat-count">{{ selectedPlayers.length }}</span>
              <GamIcon v-else :name="GamIconName.PLUS" size="20px" is-current />
            </div>

            <div v-else class="seat-ring" />
          </div>
        </div>
      </div>
    </div>

    <!-- Same band as the proposal card, so what you pick here is what the card
         will show; tapping any of it reopens the editor. -->
    <div ref="optionsToggleRef" class="chip-band" @click="isOptionsOpen = !isOptionsOpen">
      <span v-if="title" class="band-title">{{ title }}</span>
      <GamChip
        v-else
        label="Title"
        :left-icon="GamIconName.PLUS"
        :size="GamChipSize.X_SMALL"
        is-upper-case
        class="is-ghost"
      />

      <template v-if="selectedTagTypes.length">
        <GamChip
          v-for="type in selectedTagTypes"
          :key="type"
          :label="`gambit.detail.tag.category.${type}`"
          :size="GamChipSize.X_SMALL"
          is-upper-case
        />
      </template>
      <GamChip
        v-else
        label="Tags"
        :left-icon="GamIconName.PLUS"
        :size="GamChipSize.X_SMALL"
        is-upper-case
        class="is-ghost"
      />
    </div>

    <div class="meta">
      <div class="meta-head">
        <div class="when">
          <IntentDaySelect v-if="availableIntents.length > 1" v-model="selectedIntentId" :intents="availableIntents" />
          <span v-else class="day" :class="{ 'day-live': isSelectedDayLive }">{{ selectedDayLabel }}</span>
          <span class="sep">&middot;</span>

          <div class="time-span">
            <div class="hours">
              <GamButton
                :variant="GamButtonVariant.SECONDARY"
                :size="GamButtonSize.SMALL"
                @click="handlePreviousTime"
                :is-disabled="!isPreviousTimeAvailable"
                :left-icon="GamIconName.SMALL_ARROW_LEFT"
                is-icon-only
              />

              <div class="digits time-display" :aria-disabled="!hasAvailableStartTimes">
                {{ timeDisplayValue }}
              </div>

              <GamButton
                :variant="GamButtonVariant.SECONDARY"
                :size="GamButtonSize.SMALL"
                @click="handleNextTime"
                :is-disabled="!isNextTimeAvailable"
                :left-icon="GamIconName.SMALL_ARROW_RIGHT"
                is-icon-only
              />
            </div>
          </div>
        </div>

        <div v-if="chosenLocation" class="sub">
          <button type="button" class="spot" @click="handleOpenPlaceModal">{{ chosenLocation.name }}</button>
        </div>
      </div>

      <div v-if="selectedPlayers.length > 0" class="invite-context">
        {{ selectedPlayers.length }} {{ selectedPlayers.length === 1 ? 'player' : 'players' }} will be invited when you
        play
      </div>

      <div class="controls">
        <div class="tertiary">
          <GamChessSetCounter v-model="chessSetCount" />
        </div>

        <div class="primary">
          <GamButton
            :class="['action', 'icon-circle', { 'is-breathing': selectedPlayers.length > 0 }]"
            :variant="GamButtonVariant.HYPE"
            :size="GamButtonSize.LARGE"
            @click="handleCreateProposal"
            :left-icon="GamIconName.PLAY_EMPTY"
            :is-disabled="isSuggestDisabled"
            :is-loading="isSubmitting"
            is-icon-only
          />
        </div>

        <div class="secondary" />
      </div>
    </div>

    <!-- Teleported so the drawer escapes the card deck's stacking context and paints
         above the fixed bottom nav buttons. -->
    <Teleport to="body">
      <ChoosePlace
        :is-open="isPlaceDrawerOpen"
        :initial-location="chosenLocation"
        :locations="selectedIntentLocations"
        :on-confirm="handleConfirmPlace"
        :on-close="closePlaceDrawer"
        :on-choose-on-map="handleChooseOnMap"
      />
    </Teleport>

    <ClosedSpotNoticeDialog
      v-if="isClosedSpotNoticeOpen"
      :opening-hours="closedSpotHours"
      :highlight-day="closedSpotHighlightDay"
      @create-anyways="createClosedSpotAnyways"
      @change="changeClosedSpot"
    />
  </div>
</template>

<script lang="ts" setup>
import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import { GambitType } from '@/core/data/gambit/gambit.type';
import type { BaseLocationDto } from '@/core/data/location/location.interface';
import type { CreateProposalDto, SegmentLocation, SparkIntent } from '@/core/data/spark/spark.interface';
import { gambits } from '@/core/gambits';
import { isIOS, isMobile } from '@/core/helpers/ui.helper';
import { useAuthStore } from '@/stores/auth.store';
import { useMapStore } from '@/stores/map.store';
import { useSparkStore } from '@/stores/spark.store';
import GamButton from '@/views/components/GamButton.vue';
import GamChessSetCounter from '@/views/components/GamChessSetCounter.vue';
import GamChip from '@/views/components/GamChip.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamChipSize } from '@/views/composables/constants/components/gamChip.constants';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import GambitTypeSelector from '@/views/main/gambits/forms/GambitTypeSelector.vue';
import type { ChosenPlayer } from '@/views/main/gambits/modals/InvitePlayers.vue';
import InvitePlayers from '@/views/main/gambits/modals/InvitePlayers.vue';
import IntentDaySelect from '@/views/main/sparks/components/IntentDaySelect.vue';
import LocationCover from '@/views/main/sparks/components/LocationCover.vue';
import PlayerAvatar from '@/views/main/sparks/components/PlayerAvatar.vue';

import { useClosedSpotNotice } from '@/views/composables/hooks/useClosedSpotNotice';
import ClosedSpotNoticeDialog from '@/views/main/locations/modals/ClosedSpotNoticeDialog.vue';
import ChoosePlace from '@/views/main/sparks/modals/ChoosePlace.vue';
import {
  addDays,
  addHours,
  addMinutes,
  format,
  isSameDay,
  setHours,
  setMilliseconds,
  setMinutes,
  setSeconds,
  subMinutes,
} from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { storeToRefs } from 'pinia';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useModal } from 'vue-final-modal';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';

interface CustomProposalCreateResult {
  error?: {
    message?: string;
  };
  proposalId?: string;
}

interface CustomCardProps {
  intent: SparkIntent;
  intents?: SparkIntent[];
  onCreateProposal: (newProposal: CreateProposalDto) => Promise<CustomProposalCreateResult>;
  now?: Date;
}

const props = defineProps<CustomCardProps>();

// Stores

const authStore = useAuthStore();
const { userDetails } = storeToRefs(authStore);
const sparkStore = useSparkStore();
const { nearbyLocations, gpsLocations } = storeToRefs(sparkStore);
const mapStore = useMapStore();
const router = useRouter();
const {
  isOpen: isClosedSpotNoticeOpen,
  hours: closedSpotHours,
  highlightDay: closedSpotHighlightDay,
  confirmIfOutside: confirmClosedSpotIfOutside,
  createAnyways: createClosedSpotAnyways,
  change: changeClosedSpot,
} = useClosedSpotNotice();

// State

const isSubmitting = ref<boolean>(false);
const chosenStartTime = ref<Date>(new Date());
const userHasSetTime = ref(false);
const chosenLocation = ref<SegmentLocation | null>(null);
const gambitType = ref<GambitType[]>([GambitType.ANY]);
const chessSetCount = ref<number>(0);
const errorMessage = ref<string | null>(null);

const isOptionsOpen = ref(false);
const isPlaceDrawerOpen = ref(false);
const cardOptionsRef = ref<HTMLElement | null>(null);
const optionsToggleRef = ref<HTMLElement | null>(null);
const title = ref('');

const handleClickOutsideOptions = (e: MouseEvent) => {
  if (!isOptionsOpen.value) return;
  const target = e.target as HTMLElement;
  // The chip band lives outside .card-options; without this exemption the same
  // click that opens the panel is treated as an outside click and immediately closes it.
  if (cardOptionsRef.value?.contains(target)) return;
  if (optionsToggleRef.value?.contains(target)) return;
  isOptionsOpen.value = false;
};

onMounted(() => {
  document.addEventListener('click', handleClickOutsideOptions);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutsideOptions);
});

const tagOptions = [
  { value: GambitType.FREESTYLE, label: 'gambit.detail.tag.category.freestyle' },
  { value: GambitType.BLITZ, label: 'gambit.detail.tag.category.blitz' },
  { value: GambitType.RAPID, label: 'gambit.detail.tag.category.rapid' },
  { value: GambitType.BULLET, label: 'gambit.detail.tag.category.bullet' },
  { value: GambitType.CLASSIC, label: 'gambit.detail.tag.category.classic' },
  { value: GambitType.BUGHOUSE, label: 'gambit.detail.tag.category.bughouse' },
  { value: GambitType.HAND_AND_BRAIN, label: 'gambit.detail.tag.category.hand_and_brain' },
  { value: GambitType.STIMUL, label: 'gambit.detail.tag.category.stimul' },
  { value: GambitType.WALK, label: 'gambit.detail.tag.category.walk' },
  { value: GambitType.SHOGI, label: 'gambit.detail.tag.category.shogi' },
  { value: GambitType.GO, label: 'gambit.detail.tag.category.go' },
  { value: GambitType.BACKGAMMON, label: 'gambit.detail.tag.category.backgammon' },
  { value: GambitType.CATAN, label: 'gambit.detail.tag.category.catan' },
  { value: GambitType.SCRABBLE, label: 'gambit.detail.tag.category.scrabble' },
  { value: GambitType.CARDS, label: 'gambit.detail.tag.category.cards' },
  { value: GambitType.BOARD_GAMES, label: 'gambit.detail.tag.category.board_games' },
  { value: GambitType.BEGINNER, label: 'gambit.detail.tag.category.beginner' },
  { value: GambitType.INTERMEDIATE, label: 'gambit.detail.tag.category.intermediate' },
  { value: GambitType.ADVANCED, label: 'gambit.detail.tag.category.advanced' },
  { value: GambitType.LADIES_ONLY, label: 'gambit.detail.tag.category.ladies_only' },
  { value: GambitType.MUSIC, label: 'gambit.detail.tag.category.music' },
  { value: GambitType.CURATED, label: 'gambit.detail.tag.category.curated' },
  { value: GambitType.DONATIONS, label: 'gambit.detail.tag.category.donations' },
];

const selectedTagTypes = computed(() => gambitType.value.filter((type) => type !== GambitType.ANY));

const selectedPlayers = ref<ChosenPlayer[]>([]);
const CUSTOM_PROPOSAL_MIN_HOUR = 8;
const CUSTOM_PROPOSAL_MAX_START_HOUR = 22;
const TIME_ARROW_STEP_MINUTES = 30;
const INTENT_DAY_CUTOFF_HOUR = 21;
const FALLBACK_INTENT_TIMEZONE = 'UTC';

function getFloorHalfHourInTimeZone(now: Date, timeZone: string): Date {
  const zonedNow = toZonedTime(now, timeZone);
  const minuteRemainder = zonedNow.getMinutes() % TIME_ARROW_STEP_MINUTES;
  const flooredZonedNow = setSeconds(setMilliseconds(addMinutes(zonedNow, -minuteRemainder), 0), 0);

  try {
    return fromZonedTime(flooredZonedNow, timeZone);
  } catch {
    return new Date(now.getTime());
  }
}

function buildUtcDateFromIntentWallTime(date: string, hours: number, minutes: number, timeZone: string): Date | null {
  const normalizedDate = String(date).substring(0, 10);
  const normalizedHours = String(hours).padStart(2, '0');
  const normalizedMinutes = String(minutes).padStart(2, '0');
  const candidate = new Date(`${normalizedDate}T${normalizedHours}:${normalizedMinutes}:00`);

  if (Number.isNaN(candidate.getTime())) {
    return null;
  }

  try {
    return fromZonedTime(candidate, timeZone);
  } catch {
    return null;
  }
}

const getIntentTimezone = (intent: SparkIntent): string => {
  return intent.timeZone || FALLBACK_INTENT_TIMEZONE;
};

const getNowInIntentTimezone = (now: Date, timeZone: string): Date => {
  try {
    return toZonedTime(now, timeZone);
  } catch {
    return toZonedTime(now, FALLBACK_INTENT_TIMEZONE);
  }
};

const isIntentBlockedByDayCutoff = (intent: SparkIntent, now: Date): boolean => {
  const zonedNow = getNowInIntentTimezone(now, getIntentTimezone(intent));
  const intentDateKey = String(intent.date).substring(0, 10);
  const todayDateKey = format(zonedNow, 'yyyy-MM-dd');

  return intentDateKey === todayDateKey && zonedNow.getHours() >= INTENT_DAY_CUTOFF_HOUR;
};

const clockNow = computed(() => props.now ?? new Date());

function getInitialSelectedIntentId(): string {
  const sourceIntents = props.intents?.length ? props.intents : [props.intent];
  const now = clockNow.value;
  const currentUserId = userDetails.value?.id;
  const todayKey = format(now, 'yyyy-MM-dd');
  const sevenDayEndKey = format(addDays(now, 6), 'yyyy-MM-dd');

  const eligibleIntents = sourceIntents
    .filter((intent) => {
      if (currentUserId && intent.userId !== currentUserId) {
        return false;
      }

      if (isIntentBlockedByDayCutoff(intent, now)) {
        return false;
      }

      const intentDateKey = String(intent.date).substring(0, 10);
      return intentDateKey >= todayKey && intentDateKey <= sevenDayEndKey;
    })
    .sort((a, b) => a.date.localeCompare(b.date));

  if (!eligibleIntents.length) {
    return props.intent.id;
  }

  const defaultIntent = eligibleIntents.find((intent) => intent.id === props.intent.id) || eligibleIntents[0];
  return defaultIntent.id;
}

const selectedIntentId = ref<string>(getInitialSelectedIntentId());

// Computed values

const isSuggestDisabled = computed(() => {
  return (
    isSubmitting.value ||
    !chosenLocation.value ||
    !hasAvailableStartTimes.value ||
    isSelectedStartTimeInPast.value ||
    isSelectedIntentBlockedByCutoff.value
  );
});

const availableIntents = computed(() => {
  const sourceIntents = props.intents?.length ? props.intents : [props.intent];
  const currentUserId = userDetails.value?.id;
  const now = clockNow.value;
  const todayKey = format(now, 'yyyy-MM-dd');
  const sevenDayEndKey = format(addDays(now, 6), 'yyyy-MM-dd');

  const filtered = sourceIntents.filter((intent) => {
    if (currentUserId && intent.userId !== currentUserId) {
      return false;
    }

    if (isIntentBlockedByDayCutoff(intent, now)) {
      return false;
    }

    const intentDateKey = String(intent.date).substring(0, 10);
    return intentDateKey >= todayKey && intentDateKey <= sevenDayEndKey;
  });

  return filtered.slice().sort((a, b) => a.date.localeCompare(b.date));
});

const isSelectedIntentBlockedByCutoff = computed(() => {
  return isIntentBlockedByDayCutoff(selectedIntent.value, clockNow.value);
});

const selectedIntent = computed(() => {
  if (Array.isArray(props.intents) && props.intents.length > 0) {
    const latestIntent = props.intents.find((candidateIntent) => candidateIntent.id === selectedIntentId.value);

    if (latestIntent) {
      return latestIntent;
    }
  }

  return (
    availableIntents.value.find((candidateIntent) => candidateIntent.id === selectedIntentId.value) || props.intent
  );
});

/* Match IntentDaySelect labels so a single-day custom card reads the same as the picker. */
const isSelectedDayToday = computed(() => isSameDay(selectedIntent.value.date, clockNow.value));

const isSelectedDayTomorrow = computed(() => isSameDay(selectedIntent.value.date, addDays(clockNow.value, 1)));

const selectedDayLabel = computed(() => {
  if (isSelectedDayToday.value) return 'Today';
  if (isSelectedDayTomorrow.value) return 'Tomorrow';
  return format(new Date(selectedIntent.value.date), 'EEE, MMM d');
});

const isSelectedDayLive = computed(() => isSelectedDayToday.value || isSelectedDayTomorrow.value);

const selectedIntentTimeZone = computed(() => {
  return selectedIntent.value.timeZone || 'UTC';
});

const isSelectedIntentToday = computed(() => {
  const intentDateKey = String(selectedIntent.value.date).substring(0, 10);
  const todayKeyInIntentTimeZone = format(toZonedTime(clockNow.value, selectedIntentTimeZone.value), 'yyyy-MM-dd');

  return intentDateKey === todayKeyInIntentTimeZone;
});

const toSegmentLocation = (location: BaseLocationDto): SegmentLocation => {
  return {
    id: location.id,
    name: location.name,
    address: location.address,
    type: location.type,
    coordinates: location.coordinates,
    isVetted: location.isVetted,
    isFavorite: !!location.favorite,
    coverBlobHash: location.coverBlobHash || null,
    coverUpdatedAt: location.coverUpdatedAt || null,
    mapBlobHash: location.mapBlobHash || null,
  };
};

const selectedIntentLocations = computed<SegmentLocation[]>(() => {
  const seenIds = new Set<string>();
  const resolvedLocations: SegmentLocation[] = [];

  const hasNearbySparked = nearbyLocations.value.some((loc) => loc.isSparked);

  nearbyLocations.value.forEach((loc) => {
    if (seenIds.has(loc.id)) return;
    if (hasNearbySparked && !loc.isSparked && !loc.favorite) return;
    seenIds.add(loc.id);
    resolvedLocations.push(toSegmentLocation(loc));
  });

  const hasGpsSparked = gpsLocations.value.some((loc) => loc.isSparked);

  gpsLocations.value.forEach((loc) => {
    if (seenIds.has(loc.id)) return;
    if (hasGpsSparked && !loc.isSparked && !loc.favorite) return;
    seenIds.add(loc.id);
    resolvedLocations.push(toSegmentLocation(loc));
  });

  return resolvedLocations;
});

const getDateFromIntentAndHour = (date: string, hours: number, timeZone: string): Date => {
  const fromWallDate = buildUtcDateFromIntentWallTime(date, hours, 0, timeZone);

  if (fromWallDate) {
    return fromWallDate;
  }

  const [year, month, day] = date.split('-').map(Number);
  const zeroedDate =
    year && month && day
      ? new Date(year, month - 1, day, 0, 0, 0, 0)
      : setMinutes(setSeconds(setMilliseconds(new Date(date), 0), 0), 0);

  return setHours(zeroedDate, hours);
};

const startTimeRange = computed(() => {
  const now = clockNow.value;
  const minStart = getDateFromIntentAndHour(
    selectedIntent.value.date,
    CUSTOM_PROPOSAL_MIN_HOUR,
    selectedIntentTimeZone.value,
  );
  const maxStart = getDateFromIntentAndHour(
    selectedIntent.value.date,
    CUSTOM_PROPOSAL_MAX_START_HOUR,
    selectedIntentTimeZone.value,
  );
  const floorNow = getFloorHalfHourInTimeZone(now, selectedIntentTimeZone.value);
  const adjustedMinStart = isSelectedIntentToday.value && minStart.getTime() < floorNow.getTime() ? floorNow : minStart;

  return {
    min: adjustedMinStart,
    max: maxStart,
    hasAvailable: adjustedMinStart.getTime() <= maxStart.getTime(),
  };
});

const hasAvailableStartTimes = computed(() => {
  return startTimeRange.value.hasAvailable;
});

const clampStartTime = (candidateStartTime: Date): Date => {
  const { min, max, hasAvailable } = startTimeRange.value;

  if (!hasAvailable) {
    return new Date(max.getTime());
  }

  if (candidateStartTime.getTime() < min.getTime()) {
    return new Date(min.getTime());
  }

  if (candidateStartTime.getTime() > max.getTime()) {
    return new Date(max.getTime());
  }

  return new Date(candidateStartTime.getTime());
};

const getDefaultStartTime = (): Date => {
  const { min, max, hasAvailable } = startTimeRange.value;

  if (!hasAvailable) {
    return new Date(max.getTime());
  }

  if (isSelectedIntentToday.value) {
    return new Date(min.getTime());
  }

  const midpointTimestamp = min.getTime() + Math.floor((max.getTime() - min.getTime()) / 2);
  return clampStartTime(new Date(midpointTimestamp));
};

const timeDisplayValue = computed(() => {
  return format(toZonedTime(chosenStartTime.value, selectedIntentTimeZone.value), 'HH:mm');
});

const isPreviousTimeAvailable = computed(() => {
  if (!hasAvailableStartTimes.value) {
    return false;
  }

  return chosenStartTime.value.getTime() > startTimeRange.value.min.getTime();
});

const isNextTimeAvailable = computed(() => {
  if (!hasAvailableStartTimes.value) {
    return false;
  }

  return chosenStartTime.value.getTime() < startTimeRange.value.max.getTime();
});

const isSelectedStartTimeInPast = computed(() => {
  if (!hasAvailableStartTimes.value) {
    return true;
  }

  if (isSelectedIntentToday.value) {
    return chosenStartTime.value.getTime() < startTimeRange.value.min.getTime();
  }

  return chosenStartTime.value.getTime() < clockNow.value.getTime();
});

// Side-effects

watch(
  () => getFloorHalfHourInTimeZone(clockNow.value, selectedIntentTimeZone.value).getTime(),
  () => {
    if (userHasSetTime.value) {
      return;
    }

    chosenStartTime.value = getDefaultStartTime();
  },
);

watch(
  [() => selectedIntent.value.date, selectedIntentTimeZone],
  ([newDate, newTz], oldValues) => {
    if (userHasSetTime.value && oldValues) {
      const prevTz = oldValues[1] || newTz;
      const zonedPrev = toZonedTime(chosenStartTime.value, prevTz);
      const wallHours = zonedPrev.getHours();
      const wallMinutes = zonedPrev.getMinutes();
      const candidate = buildUtcDateFromIntentWallTime(String(newDate).substring(0, 10), wallHours, wallMinutes, newTz);
      if (candidate) {
        chosenStartTime.value = clampStartTime(candidate);
        return;
      }
    }
    chosenStartTime.value = getDefaultStartTime();
  },
  { immediate: true },
);

watch(
  availableIntents,
  (nextAvailableIntents) => {
    if (!nextAvailableIntents.length) {
      return;
    }

    const hasSelectedIntent = nextAvailableIntents.some(
      (candidateIntent) => candidateIntent.id === selectedIntentId.value,
    );

    if (!hasSelectedIntent) {
      selectedIntentId.value = nextAvailableIntents[0].id;
    }
  },
  { immediate: true },
);

watch(
  () => props.intent.id,
  () => {
    selectedIntentId.value = props.intent.id;
    title.value = '';
    gambitType.value = [GambitType.ANY];
    isOptionsOpen.value = false;
  },
);

watch(
  selectedIntentLocations,
  (newChosenLocations) => {
    if (!newChosenLocations.length) {
      if (!chosenLocation.value) {
        return;
      }
      return;
    }

    if (chosenLocation.value) {
      const currentChoice = newChosenLocations.find((location) => location.id === chosenLocation.value?.id);

      if (currentChoice) {
        chosenLocation.value = currentChoice;
        return;
      }
    }

    chosenLocation.value = newChosenLocations[0] || null;
  },
  { immediate: true },
);

// Actions

function handlePreviousTime() {
  if (!isPreviousTimeAvailable.value) return;

  userHasSetTime.value = true;
  const zonedTime = toZonedTime(chosenStartTime.value, selectedIntentTimeZone.value);
  const minuteRemainder = zonedTime.getMinutes() % TIME_ARROW_STEP_MINUTES;
  const isOnHalfHour = minuteRemainder === 0 && zonedTime.getSeconds() === 0 && zonedTime.getMilliseconds() === 0;
  const minutesToSubtract = isOnHalfHour ? TIME_ARROW_STEP_MINUTES : minuteRemainder;

  chosenStartTime.value = clampStartTime(subMinutes(chosenStartTime.value, minutesToSubtract));
}

function handleNextTime() {
  if (!isNextTimeAvailable.value) return;

  userHasSetTime.value = true;
  const zonedTime = toZonedTime(chosenStartTime.value, selectedIntentTimeZone.value);
  const minuteRemainder = zonedTime.getMinutes() % TIME_ARROW_STEP_MINUTES;
  const isOnHalfHour = minuteRemainder === 0 && zonedTime.getSeconds() === 0 && zonedTime.getMilliseconds() === 0;
  const minutesToAdd = isOnHalfHour ? TIME_ARROW_STEP_MINUTES : TIME_ARROW_STEP_MINUTES - minuteRemainder;

  chosenStartTime.value = clampStartTime(addMinutes(chosenStartTime.value, minutesToAdd));
}

function handleConfirmPlace(locationId: string) {
  const location = selectedIntentLocations.value.find((loc) => loc.id === locationId);

  if (!location) return;

  chosenLocation.value = location;

  closePlaceDrawer();
}

function closePlaceDrawer() {
  isPlaceDrawerOpen.value = false;
}

function handleOpenPlaceModal() {
  isPlaceDrawerOpen.value = true;
}

function handleChooseOnMap() {
  closePlaceDrawer();
  router.push({ name: RouterNameType.LOCATIONS });
  mapStore.enterProposalMode();
}

async function handleCreateProposal() {
  if (!chosenLocation.value || !hasAvailableStartTimes.value || isSelectedStartTimeInPast.value) return;
  if (isSelectedIntentBlockedByCutoff.value) return;

  errorMessage.value = null;
  isSubmitting.value = true;

  try {
    const proceed = await confirmClosedSpotIfOutside({
      locationId: chosenLocation.value.id,
      start: chosenStartTime.value,
      timeZone: selectedIntentTimeZone.value,
    });
    if (!proceed) return;

    const zonedStart = toZonedTime(chosenStartTime.value, selectedIntentTimeZone.value);
    const zonedEnd = toZonedTime(addHours(chosenStartTime.value, 2), selectedIntentTimeZone.value);

    const response = await props.onCreateProposal({
      start: format(zonedStart, 'yyyy-MM-dd HH:mm'),
      end: format(zonedEnd, 'yyyy-MM-dd HH:mm'),
      locationId: chosenLocation.value?.id,
      intentId: selectedIntent.value.id,
      gambitType: gambitType.value,
      ownerChessSetCount: chessSetCount.value,
      title: title.value || undefined,
    });

    if (response.error) {
      errorMessage.value = response.error.message || 'Could not create proposal. Please try again.';
    } else {
      title.value = '';
      gambitType.value = [GambitType.ANY];
      isOptionsOpen.value = false;

      if (response.proposalId && selectedPlayers.value.length > 0) {
        const participantIds = selectedPlayers.value.map((p) => p.id);
        await gambits.sparkService.createInvitations(response.proposalId, participantIds).catch(() => {});
      }
      selectedPlayers.value = [];
    }
  } catch {
    errorMessage.value = 'Could not create proposal. Please try again.';
  } finally {
    isSubmitting.value = false;
  }

  if (errorMessage.value) {
    useToast().error(errorMessage.value);
  }
}

interface PlayerCircle {
  id: string;
  username: string;
  profileImageBlobHash: string | null;
}

const MAX_VISIBLE_CIRCLES = 4;

const playerCircles = computed<(PlayerCircle | null)[]>(() => {
  const circles: (PlayerCircle | null)[] = selectedPlayers.value.slice(0, MAX_VISIBLE_CIRCLES - 1).map((p) => ({
    id: p.id,
    username: p.username,
    profileImageBlobHash: p.profileImageBlobHash ?? null,
  }));

  while (circles.length < MAX_VISIBLE_CIRCLES) {
    circles.push(null);
  }

  return circles;
});

function handleOpenInviteModal() {
  const { open, close } = useModal({
    component: InvitePlayers,
    attrs: {
      participantIds: [],
      initialPlayers: [...selectedPlayers.value],
      mode: 'select' as const,
      onSelect: (players: ChosenPlayer[]) => {
        selectedPlayers.value = players;
      },
      onClose: () => close(),
    },
  });
  open();
}
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.CustomProposalCard {
  position: relative;
  display: flex;
  flex-direction: column;
  text-align: left;
  gap: var(--card-stack-gap);
  background: transparent;
  overflow: visible;
}

.CustomProposalCard.spark-card {
  box-shadow: none;
}

.cover {
  position: relative;
  width: 100%;
  flex: 1 1 0;
  min-height: 0;
  border-radius: var(--radius-normal);
  overflow: hidden;
  background: var(--color-dark-600);
}

.location-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  mask-image: linear-gradient(black 0%, black 75%, transparent 100%);
}

.cover-foot {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  /* Above the closed .card-options layer so the invite ring stays tappable. */
  z-index: 4;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.5rem var(--card-gutter) var(--card-foot-inset);
  background: linear-gradient(transparent 0%, rgba(28, 28, 28, 0.55) 100%);
  pointer-events: none;
}

.cover-foot > * {
  pointer-events: auto;
}

/* Bottom-aligned: a filled seat carries a badge row above its face, so centring
   would drop that face below the empty rings beside it. */
.cover-players {
  display: flex;
  align-items: flex-end;
  gap: clamp(0.25rem, min(1.75cqw, 1.17cqh), 0.5rem);
}

.seat {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  position: relative;
  width: var(--card-avatar-size);
}

.chip-band {
  flex: none;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: var(--spacing-xs);
  height: var(--card-band-height);
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;
  cursor: pointer;

  &::-webkit-scrollbar {
    display: none;
  }

  > * {
    flex-shrink: 0;
  }

  :deep(.gam-chip) {
    cursor: pointer;
  }

  /* Placeholders for what has not been added yet, quieter than real tags. */
  :deep(.gam-chip.is-ghost) {
    background: transparent;
    box-shadow: inset 0 0 0 1px var(--color-white-20);
    color: var(--color-white-60);
  }
}

.band-title {
  max-width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: var(--spacing-xxs);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--color-white);
}

.seat-ring {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-white-20);
  color: var(--color-white-40);
}

.seat-ring.is-invite {
  border-style: dashed;
  border-color: var(--color-white-30);
  color: var(--color-white-60);
  cursor: pointer;
  transition: color 0.15s ease;
}

@include hover-capable {
  .seat-ring.is-invite:hover {
    color: var(--color-white);
  }
}

.seat-count {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.03em;
}

.meta {
  flex: none;
  height: max(var(--card-meta-min), calc(50cqw - var(--card-stack-gap)));
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: clamp(0.4rem, min(2.5cqw, 1.67cqh), 0.75rem);
  padding: var(--card-gutter);
  border-radius: var(--radius-normal);
  background: var(--color-card-surface);
}

.meta-head {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.when {
  display: flex;
  align-items: center;
  gap: 0.3em;
  font-size: clamp(1.05rem, min(6cqw, 4cqh), 1.5rem);
  letter-spacing: 0.02em;
  color: var(--color-white);
  white-space: nowrap;
}

.when .sep {
  color: var(--color-white-30);
  flex: none;
}

.when .day-live {
  color: var(--color-pink);
  font-weight: 600;
}

.when :deep(.intent-picker) {
  flex: none;
  color: inherit;
  font: inherit;
}

/* Time stepper at text scale: a strip that reads like the day/time line rather
   than the large stepper it was on the cover. */
.time-span {
  display: flex;
  align-items: center;
  min-width: 0;
}

.time-span .hours {
  font-size: inherit;
  font-weight: inherit;
  letter-spacing: inherit;
  color: inherit;
  display: flex;
  align-items: center;
  gap: 0.25em;
}

.time-span .time-display {
  background: transparent;
  color: inherit;
  font: inherit;
  letter-spacing: inherit;
  text-align: center;
  width: 3.4em;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.time-span .time-display[aria-disabled='true'] {
  opacity: 0.5;
}

.sub {
  display: flex;
  align-items: baseline;
  min-width: 0;
  font-size: clamp(0.75rem, min(4cqw, 2.67cqh), 0.875rem);
  letter-spacing: 0.02em;
}

.sub .spot {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  height: auto;
  padding: 0.2em 0.65em;
  border-radius: 30px;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  color: var(--color-white-70);
  font: inherit;
  letter-spacing: inherit;
  cursor: pointer;
  transition: all 200ms linear;
}

@include hover-capable {
  .sub .spot:hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }
}

.invite-context {
  font-size: 11px;
  letter-spacing: 0.05em;
  color: var(--color-white-40);
  text-align: center;
  min-height: 1.1em;
}

.controls {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
}

.tertiary,
.primary,
.secondary {
  display: flex;
  align-items: center;
}

.tertiary {
  grid-column: 1;
  justify-self: start;
  min-width: 0;
}

.primary {
  grid-column: 2;
  justify-self: center;
}

.secondary {
  grid-column: 3;
}

.primary :deep(.gam-button-container.is-icon) {
  width: var(--card-play-size) !important;
  height: var(--card-play-size) !important;
  min-width: var(--card-play-size) !important;
  min-height: var(--card-play-size) !important;
}

.primary :deep(.svg-container) {
  font-size: var(--card-play-icon) !important;
  width: var(--card-play-icon) !important;
  height: var(--card-play-icon) !important;
  min-width: var(--card-play-icon) !important;
}

.icon-circle :deep(.gam-button-container) {
  border-radius: 50% !important;

  .icon {
    position: absolute !important;
    left: 50% !important;
    top: 50% !important;
    transform: translate(-50%, -50%) !important;
  }
}

.is-breathing :deep(.gam-button-container) {
  animation: breathe 4s ease-in-out infinite;
}

@keyframes breathe {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 oklch(0.72 0.19 var(--hue, 0) / 0.4);
  }
  50% {
    transform: scale(1.06);
    box-shadow: 0 0 12px 4px oklch(0.72 0.19 var(--hue, 0) / 0.25);
  }
}

input.inline-title-input {
  display: block;
  width: 100%;
  min-height: 47px;
  border: 1px solid var(--color-white-20);
  border-radius: 25px;
  background: transparent;
  color: var(--color-white);
  font-size: 14px;
  padding: 0 20px;
  outline: none;
  box-sizing: border-box;
  text-align: left;
  transition: border-color 200ms;

  &::placeholder {
    color: var(--color-white-50);
  }

  &:focus {
    border-color: var(--color-pink);
  }

  @include hover {
    border-color: var(--color-pink);
  }
}

.inline-tags {
  width: 100%;
}

.card-options {
  position: absolute;
  inset: 0;
  z-index: 3;
  padding: calc(var(--card-gutter) * 0.36) var(--card-gutter) calc(var(--card-gutter) * 0.91);
  box-sizing: border-box;
  background: transparent;
  border-radius: var(--radius-normal);
  pointer-events: none;
  transition: background 200ms ease;
}

/* Beats .cover-foot once open so the title input and tag selector are reachable;
   below it when closed so the pills can open the panel. */
.card-options.is-open {
  z-index: 5;
  background: var(--color-dark-650);
  pointer-events: auto;
}

.card-options-close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--color-white-50);
  transition: color 200ms ease;
  z-index: 2;

  @include hover {
    color: var(--color-white);
  }

  @include press;
}

.card-options-scroll {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 0.75rem;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.card-options.is-open .card-options-scroll {
  overflow-y: auto;
  pointer-events: auto;
}

.options-expanded {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  pointer-events: auto;
  animation: options-fade-in 200ms ease;
}

@keyframes options-fade-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
